import { useMemo, useState } from 'react';
import { useModal } from '../../../helpers/hooks/modalContext';
import type { GiftBitBrand } from '../../../helpers/utils/types';
import { stripHtml } from '../../../helpers/utils/sanitize';
import {
  GIFT_CARD_FILTERS,
  getGiftCardCategory,
  type GiftCardCategory,
  type GiftCardFilterId,
} from '../utils/giftCardCategories';

type GiftCardsSectionProps = {
  search: string;
  onSearchChange: (value: string) => void;
  loading: boolean;
  cards: GiftBitBrand[];
};

type GiftCardWithCategory = GiftBitBrand & { category: GiftCardCategory };

const SearchIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <circle cx='11' cy='11' r='8' />
    <line x1='21' y1='21' x2='16.65' y2='16.65' />
  </svg>
);

export const GiftCardsSection = ({
  search,
  onSearchChange,
  loading,
  cards,
}: GiftCardsSectionProps) => {
  const { setModal } = useModal();
  const [activeCategory, setActiveCategory] = useState<GiftCardFilterId>('all');

  const filteredCards = useMemo((): GiftCardWithCategory[] => {
    const normalizedSearch = search.trim().toLowerCase();
    return cards
      .map((card) => ({
        ...card,
        category: getGiftCardCategory(card.name, card.brand_code),
      }))
      .filter((card) => {
        const matchesCategory = activeCategory === 'all' || card.category === activeCategory;
        const matchesSearch =
          !normalizedSearch ||
          card.name.toLowerCase().includes(normalizedSearch) ||
          card.brand_code.toLowerCase().includes(normalizedSearch);
        return matchesCategory && matchesSearch;
      });
  }, [activeCategory, cards, search]);

  const openGiftCard = (card: GiftBitBrand) => {
    setModal('all-gift-cards', { brandCode: card.brand_code });
  };

  return (
    <div className='rd-panel rd-gc-panel rd-gc-light' id='rd-gift-cards'>
      <div className='rd-sec'>
        <div>
          <h2 className='rd-sec-title'>Gift cards</h2>
          <p className='rd-sec-sub'>Redeem points for partner gift cards.</p>
        </div>
        <span className='rd-gc-sec-pill'>115+ brands</span>
      </div>

      <div className='rd-search-wrap'>
        <span className='rd-search-icon'>
          <SearchIcon />
        </span>
        <input
          className='rd-search'
          placeholder='Search gift cards...'
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </div>

      <div className='rd-gc-filters'>
        {GIFT_CARD_FILTERS.map((filter) => (
          <button
            key={filter.id}
            type='button'
            className={`rd-gc-chip${activeCategory === filter.id ? ' on' : ''}`}
            onClick={() => setActiveCategory(filter.id)}
          >
            <span className='rd-gc-chip-ic'>{filter.icon}</span>
            {filter.label}
          </button>
        ))}
      </div>

      <div className={`rd-gc-grid${filteredCards.length === 0 ? ' is-empty' : ''}`}>
        {loading && cards.length === 0
          ? Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className='rd-gc rd-gc-skeleton' aria-hidden='true'>
                <div className='rd-gc-head' />
                <div className='rd-gc-body'>
                  <span className='rd-gc-name'>Loading…</span>
                </div>
              </div>
            ))
          : filteredCards.map((card) => (
              <button
                key={card.brand_code}
                type='button'
                className='rd-gc'
                data-category={card.category}
                data-name={card.name.toLowerCase()}
                onClick={() => openGiftCard(card)}
                title={stripHtml(card.disclaimer || '') || card.name}
              >
                <div className='rd-gc-head'>
                  {card.image_url ? (
                    <img className='rd-gc-logo' src={card.image_url} alt={card.name} />
                  ) : (
                    <span className='rd-gc-fallback'>{card.name.charAt(0)}</span>
                  )}
                </div>
                <div className='rd-gc-body'>
                  <span className='rd-gc-name'>{card.name}</span>
                  <span className='rd-gc-pts'>Redeem with points</span>
                </div>
              </button>
            ))}
      </div>

      {!loading && filteredCards.length === 0 && (
        <div className='rd-gc-empty is-visible'>No brands found</div>
      )}
    </div>
  );
};
